import { useState, useEffect } from "react"
import { FileText, Image as ImageIcon, Video, HelpCircle, Download, X, Save, Loader2, Plus, Trash2, Languages } from "lucide-react"
import { Button } from "../ui/button"
import TipTapEditor from "../TipTapEditor"
import ImageUpload from "../ImageUpload"
import VideoUpload from "../VideoUpload"

const BLOCK_TYPES = [
  { type: 'TEXT', label: 'Bloc de Texte', icon: FileText, color: 'blue' },
  { type: 'IMAGE', label: 'Bloc d\'Image', icon: ImageIcon, color: 'green' },
  { type: 'VIDEO', label: 'Bloc Vidéo', icon: Video, color: 'purple' },
  { type: 'ASSIGNMENT', label: 'Bloc Quiz', icon: HelpCircle, color: 'orange' },
  { type: 'DOWNLOAD', label: 'Bloc de Téléchargement', icon: Download, color: 'gray' },
]

export default function BlockForm({
  block = null, // null pour création, objet pour édition
  blockType,
  onSave,
  onCancel,
  blocksCount = 0,
  existingBlocks = [], // Liste des blocs existants pour calculer l'ordre
  onShowTranslations
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: blockType || 'TEXT',
    content: '',
    imageUrl: '',
    videoUrl: '',
    duration: 0,
    order: 1
  })
  // Pour DOWNLOAD : gestion des fichiers
  const [downloadFiles, setDownloadFiles] = useState([
    { name: '', url: '', size: '', type: '', description: '' }
  ])
  // Pour IMAGE et VIDEO : gestion des fichiers uploadés
  const [imageFile, setImageFile] = useState(null)
  const [videoFile, setVideoFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (block) {
      // Le champ content est maintenant inclus dans la réponse de l'API GET /modules?courseId=...
      // Extraire le contenu selon le type de bloc
      let content = ""
      let imageUrl = ""
      let videoUrl = ""
      
      if (block.content) {
        switch (block.type) {
          case 'TEXT':
            // Pour les blocs TEXT, le content contient : html, wordCount, readingTime, attachments, resources, etc.
            // Extraire le contenu HTML
            if (typeof block.content === 'string') {
              content = block.content
            } else if (block.content && typeof block.content === 'object' && block.content.html) {
              // Le content est un objet avec la propriété html
              content = block.content.html || ""
            } else {
              content = ""
            }
            break
          case 'IMAGE':
            // Pour les blocs IMAGE, le content contient : imageUrl, alt, caption, resources, zoomable, annotations, etc.
            imageUrl = block.content.imageUrl || ""
            content = block.content.caption || ""
            // Réinitialiser imageFile pour forcer l'affichage de l'image existante
            setImageFile(null)
            break
          case 'VIDEO':
            // Pour les blocs VIDEO, le content contient : videoUrl, transcript, resources, subtitles, thumbnail, etc.
            videoUrl = block.content.videoUrl || ""
            content = block.content.transcript || ""
            break
          case 'ASSIGNMENT':
            // Pour les blocs ASSIGNMENT, le content contient : instructions, hints, expectedOutput, etc.
            content = block.content.instructions || ""
            break
          case 'DOWNLOAD':
            // Pour les blocs DOWNLOAD, le content contient : files, instructions, etc.
            content = block.content.instructions || ""
            // Charger les fichiers si disponibles
            if (block.content.files && Array.isArray(block.content.files)) {
              setDownloadFiles(block.content.files.length > 0 
                ? block.content.files 
                : [{ name: '', url: '', size: '', type: '', description: '' }])
            }
            break
          default:
            content = typeof block.content === 'string' ? block.content : ""
        }
      }
      
      setFormData({
        title: block.title || '',
        description: block.description || '',
        content: content,
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        duration: block.duration || 0,
        order: block.order || 1
      })
    } else {
      // Calculer l'ordre initial pour un nouveau bloc
      const maxOrder = existingBlocks.length > 0
        ? Math.max(...existingBlocks.map(b => b.order || 0))
        : 0
      const initialOrder = maxOrder + 1

      setFormData({
        title: '',
        description: '',
        content: '',
        imageUrl: '',
        videoUrl: '',
        duration: 0,
        order: initialOrder
      })
      setDownloadFiles([{ name: '', url: '', size: '', type: '', description: '' }])
    }
  }, [block, blockType, existingBlocks])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleContentChange = (html) => {
    // S'assurer que le contenu est bien du HTML (chaîne de caractères)
    // TipTapEditor.getHTML() retourne toujours une chaîne HTML
    if (typeof html === 'string') {
      setFormData(prev => ({ ...prev, content: html }))
    } else {
      console.warn('TipTapEditor returned non-string content:', html)
      setFormData(prev => ({ ...prev, content: '' }))
    }
  }

  const handleImageFileSelect = (file) => {
    if (file instanceof File) {
      setImageFile(file)
      // Créer une preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, imageUrl: e.target.result }))
      }
      reader.readAsDataURL(file)
    } else {
      setImageFile(null)
      setFormData(prev => ({ ...prev, imageUrl: file || '' }))
    }
  }

  const handleVideoFileSelect = (file) => {
    if (file instanceof File) {
      setVideoFile(file)
      // Pour les vidéos, on peut créer une preview ou juste stocker le fichier
      // L'URL sera générée par le backend après upload
    } else {
      setVideoFile(null)
      setFormData(prev => ({ ...prev, videoUrl: file || '' }))
    }
  }

  // Gestion des fichiers pour DOWNLOAD
  const handleDownloadFileChange = (index, field, value) => {
    setDownloadFiles(prev => prev.map((file, i) => 
      i === index ? { ...file, [field]: value } : file
    ))
  }

  const handleAddDownloadFile = () => {
    setDownloadFiles(prev => [...prev, { name: '', url: '', size: '', type: '', description: '' }])
  }

  const handleRemoveDownloadFile = (index) => {
    setDownloadFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Fonction pour calculer wordCount et readingTime depuis HTML
  const calculateTextStats = (html) => {
    if (!html) return { wordCount: 0, readingTime: 0 }
    // Supprimer les balises HTML et extraire le texte
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    const words = text.split(' ').filter(word => word.length > 0)
    const wordCount = words.length
    const readingTime = Math.ceil(wordCount / 200) // 200 mots par minute
    return { wordCount, readingTime }
  }

  // Fonction pour vérifier si le HTML contient du contenu réel (pas juste des balises vides)
  const hasValidHTMLContent = (html) => {
    if (!html || typeof html !== 'string') return false
    // Supprimer les balises HTML et les espaces
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
    // Vérifier qu'il reste du texte
    return text.length > 0
  }


  const validate = () => {
    const newErrors = {}
    // Titre et description sont maintenant optionnels
    
    const blockTypeToUse = block?.type || blockType || 'TEXT'
    
    // Validation spécifique selon le type
    if (blockTypeToUse === 'VIDEO' && !formData.videoUrl.trim() && !videoFile) {
      newErrors.videoUrl = 'Une vidéo est requise (upload ou URL)'
    }
    
    if (blockTypeToUse === 'IMAGE' && !formData.imageUrl.trim() && !imageFile) {
      newErrors.imageUrl = 'Une image est requise (upload ou URL)'
    }
    
    if (blockTypeToUse === 'TEXT') {
      if (!formData.content || !formData.content.trim()) {
        newErrors.content = 'Le contenu est obligatoire'
      } else if (!hasValidHTMLContent(formData.content)) {
        newErrors.content = 'Le contenu doit contenir du texte (pas seulement des balises vides)'
      }
    }
    
    if (blockTypeToUse === 'DOWNLOAD') {
      const validFiles = downloadFiles.filter(f => f.name.trim() && f.url.trim())
      if (validFiles.length === 0) {
        newErrors.downloadFiles = 'Au moins un fichier est requis'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    try {
      // Créer le contenu selon le type de bloc (comme dans ContentBlockModal)
      const blockTypeToUse = block?.type || blockType || 'TEXT'
      let content = {}
      
      switch (blockTypeToUse) {
        case 'TEXT': {
          // S'assurer que le contenu HTML est valide et non vide
          // formData.content devrait être une chaîne HTML provenant de TipTapEditor.getHTML()
          let htmlContent = ''
          
          if (typeof formData.content === 'string') {
            htmlContent = formData.content
          } else if (formData.content && typeof formData.content === 'object' && formData.content.html) {
            // Fallback si content est un objet avec une propriété html
            htmlContent = formData.content.html
          } else {
            htmlContent = ''
          }
          
          // Nettoyer le contenu HTML pour s'assurer qu'il contient du texte réel
          if (!htmlContent || !hasValidHTMLContent(htmlContent)) {
            // Si le contenu est vide ou ne contient que des balises vides, utiliser un paragraphe par défaut
            htmlContent = '<p>Contenu du bloc</p>'
          }
          
          const textStats = calculateTextStats(htmlContent)
          content = {
            html: htmlContent, // Toujours envoyer le HTML comme chaîne de caractères
            wordCount: textStats.wordCount,
            readingTime: textStats.readingTime
          }
          break
        }
        case 'IMAGE':
          // Selon la spécification API : content: { alt: "Description", caption: "Légende" }
          // Si un fichier est uploadé, on ne met pas imageUrl dans content (le backend l'ajoutera)
          // Si pas de fichier, on peut mettre l'URL dans content.imageUrl (mais pas de data URL)
          if (imageFile) {
            // Upload de fichier : content ne contient que alt et caption
            // Le backend générera automatiquement imageUrl
            content = {
              alt: formData.title || "Image",
              caption: formData.content || ""
            }
          } else {
            // Pas de nouveau fichier : inclure imageUrl dans content seulement si c'est une URL HTTP valide
            // Ne pas inclure les data URLs (base64) car le backend ne les accepte pas
            const imageUrl = formData.imageUrl || ""
            if (imageUrl && !imageUrl.startsWith('data:')) {
              // URL HTTP valide : inclure dans content
              content = {
                imageUrl: imageUrl,
                alt: formData.title || "Image",
                caption: formData.content || ""
              }
            } else {
              // Pas d'URL valide ou data URL : seulement alt et caption
              // Le backend conservera l'imageUrl existante
              content = {
                alt: formData.title || "Image",
                caption: formData.content || ""
              }
            }
          }
          break
        case 'VIDEO':
          // Selon la spécification API : content: { transcript: "Transcription" }
          // Si un fichier est uploadé, on ne met pas videoUrl dans content (le backend l'ajoutera)
          // Si pas de fichier, on peut mettre l'URL dans content.videoUrl
          if (videoFile) {
            // Upload de fichier : content ne contient que transcript
            content = {
              transcript: formData.content || ""
            }
          } else {
            // URL fournie : inclure videoUrl dans content
            content = {
              videoUrl: formData.videoUrl || "",
              transcript: formData.content || ""
            }
          }
          // duration peut être dans content ou au niveau du bloc selon l'API
          if (formData.duration > 0) {
            content.duration = formData.duration
          }
          break
        case 'ASSIGNMENT':
          content = {
            instructions: formData.content,
            hints: [],
            expectedOutput: ""
          }
          break
        case 'DOWNLOAD': {
          const validFiles = downloadFiles.filter(f => f.name.trim() && f.url.trim())
          content = {
            files: validFiles.map(f => ({
              name: f.name,
              url: f.url,
              size: f.size || '',
              type: f.type || ''
            })),
            instructions: formData.content || "",
            resources: []
          }
          break
        }
        default:
          content = { content: formData.content }
      }
      
      const saveData = {
        title: formData.title,
        description: formData.description || '',
        type: blockTypeToUse,
        content: content,
        order: parseInt(formData.order, 10) || 1,
        duration: formData.duration || 0,
        isPublished: true
      }

      // Les fichiers (image, video) peuvent être envoyés lors de la création ET de la mise à jour
      // L'API attend 'image' et 'video' (pas 'imageFile' et 'videoFile')
      // Selon la spécification API, lors de la mise à jour avec un nouveau fichier, on utilise multipart/form-data
      if (blockTypeToUse === 'IMAGE' && imageFile) {
        saveData.image = imageFile
      }
      if (blockTypeToUse === 'VIDEO' && videoFile) {
        saveData.video = videoFile
      }

      // LOG: Données préparées dans BlockForm
      console.log('[BlockForm] Données préparées pour sauvegarde:', {
        isUpdate: !!block,
        blockId: block?.id,
        blockType: blockTypeToUse,
        saveData: {
          ...saveData,
          image: saveData.image ? {
            name: saveData.image.name,
            type: saveData.image.type,
            size: saveData.image.size,
            isFile: saveData.image instanceof File
          } : undefined,
          video: saveData.video ? {
            name: saveData.video.name,
            type: saveData.video.type,
            size: saveData.video.size,
            isFile: saveData.video instanceof File
          } : undefined,
          content: saveData.content
        },
        hasImageFile: !!imageFile,
        hasVideoFile: !!videoFile
      })

      await onSave(saveData)
    } catch (error) {
      console.error('Error saving block:', error)
    } finally {
      setSaving(false)
    }
  }

  const blockTypeToUse = block?.type || blockType || 'TEXT'
  const selectedBlockType = BLOCK_TYPES.find(bt => bt.type === blockTypeToUse) || BLOCK_TYPES[0]
  const Icon = selectedBlockType.icon

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {block ? 'Modifier le bloc' : `Nouveau ${selectedBlockType.label}`}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {block ? 'Modifiez les informations du bloc' : `Créez un nouveau bloc de type ${selectedBlockType.label.toLowerCase()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {block && onShowTranslations && (
              <Button
                variant="outline"
                size="sm"
                onClick={onShowTranslations}
                className="h-8"
                title="Gérer les traductions du bloc"
              >
                <Languages className="w-4 h-4 mr-2" />
                Traductions
              </Button>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            {/* Block Type Selection */}
            {!block && !blockType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de bloc <span className="text-gray-700">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {BLOCK_TYPES.map((bt) => {
                    const BlockIcon = bt.icon
                    return (
                      <button
                        key={bt.type}
                        type="button"
                        onClick={() => {
                          setFormData({
                            title: formData.title,
                            content: '',
                            imageUrl: '',
                            videoUrl: ''
                          })
                        }}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                          blockTypeToUse === bt.type
                            ? 'border-[#141835] bg-[#141835]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <BlockIcon className={`w-5 h-5 ${blockTypeToUse === bt.type ? 'text-[#141835]' : 'text-gray-400'}`} />
                        <span className={`font-medium ${blockTypeToUse === bt.type ? 'text-[#141835]' : 'text-gray-700'}`}>
                          {bt.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                  errors.title ? 'border-gray-500' : 'border-gray-300'
                }`}
                placeholder={`Ex: ${blockTypeToUse === 'TEXT' ? 'Introduction au concept' : blockTypeToUse === 'VIDEO' ? 'Vidéo d\'introduction' : 'Titre du bloc'} (optionnel)`}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-gray-700">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Description du bloc (optionnel)"
              />
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ordre
              </label>
              <input
                type="number"
                name="order"
                value={formData.order || ''}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                placeholder="Ordre du bloc"
              />
              <p className="mt-1 text-xs text-gray-500">
                Position du bloc dans la leçon (doit être unique)
              </p>
            </div>

            {/* Duration */}
            {blockTypeToUse === 'VIDEO' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée (minutes)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration || ''}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  placeholder="15"
                />
              </div>
            )}

            {/* Contenu selon le type */}
            {blockTypeToUse === 'TEXT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu textuel <span className="text-red-500">*</span>
                </label>
                <TipTapEditor
                  key={block?.id || 'new-block'} // Forcer le re-render quand le bloc change
                  content={formData.content || ''}
                  onChange={handleContentChange}
                  placeholder="Saisissez le contenu textuel de votre bloc..."
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
              </div>
            )}

            {blockTypeToUse === 'IMAGE' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uploader une image <span className="text-red-500">*</span>
                  </label>
                  <ImageUpload
                    currentImage={formData.imageUrl}
                    onImageSelect={handleImageFileSelect}
                  />
                  {imageFile && (
                    <p className="text-sm text-[#203a8a] mt-2">
                      Fichier sélectionné : {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                  {formData.imageUrl && !imageFile && (
                    <p className="text-sm text-gray-500 mt-2">
                      URL actuelle : {formData.imageUrl.substring(0, 50)}...
                    </p>
                  )}
                  {errors.imageUrl && (
                    <p className="mt-1 text-sm text-red-600">{errors.imageUrl}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Légende
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Légende de l'image (optionnel)"
                  />
                </div>
              </div>
            )}

            {blockTypeToUse === 'VIDEO' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uploader une vidéo ou URL <span className="text-red-500">*</span>
                  </label>
                  <VideoUpload
                    currentVideo={formData.videoUrl}
                    onVideoSelect={handleVideoFileSelect}
                    placeholder="Cliquez pour sélectionner une vidéo (max 100MB)"
                  />
                  {videoFile && (
                    <p className="text-sm text-[#203a8a] mt-2">
                      Fichier sélectionné : {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                  {!videoFile && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-2">Ou entrez une URL :</p>
                      <input
                        type="url"
                        name="videoUrl"
                        value={formData.videoUrl}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                          errors.videoUrl ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="https://youtube.com/watch?v=... ou https://vimeo.com/..."
                      />
                    </div>
                  )}
                  {errors.videoUrl && (
                    <p className="mt-1 text-sm text-red-600">{errors.videoUrl}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transcription
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Transcription de la vidéo (optionnel)"
                  />
                </div>
              </div>
            )}

            {blockTypeToUse === 'ASSIGNMENT' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions du devoir
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  placeholder="Décrivez les instructions du devoir, les objectifs, les critères d'évaluation..."
                />
                <p className="text-sm text-gray-500 mt-2">
                  Vous pourrez ajouter des indices et des exemples de sortie attendue plus tard
                </p>
              </div>
            )}

            {blockTypeToUse === 'DOWNLOAD' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructions de téléchargement
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Instructions pour les téléchargements (optionnel)"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Fichiers à télécharger <span className="text-red-500">*</span>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddDownloadFile}
                      className="flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter un fichier
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {downloadFiles.map((file, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Fichier {index + 1}</span>
                          {downloadFiles.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveDownloadFile(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Nom du fichier *</label>
                            <input
                              type="text"
                              value={file.name}
                              onChange={(e) => handleDownloadFileChange(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                              placeholder="exemple.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">URL *</label>
                            <input
                              type="url"
                              value={file.url}
                              onChange={(e) => handleDownloadFileChange(index, 'url', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                              placeholder="https://example.com/file.pdf"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Taille</label>
                            <input
                              type="text"
                              value={file.size}
                              onChange={(e) => handleDownloadFileChange(index, 'size', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                              placeholder="2.5 MB"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Type</label>
                            <input
                              type="text"
                              value={file.type}
                              onChange={(e) => handleDownloadFileChange(index, 'type', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                              placeholder="PDF, JavaScript, etc."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            value={file.description}
                            onChange={(e) => handleDownloadFileChange(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                            placeholder="Description du fichier (optionnel)"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {errors.downloadFiles && (
                    <p className="mt-1 text-sm text-red-600">{errors.downloadFiles}</p>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-2">
                    Au moins un fichier avec nom et URL est requis
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {block ? 'Modification...' : 'Création...'}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {block ? 'Modifier' : 'Créer le bloc'}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

